import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from "@material-ui/core";
import { HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { MakeoverEngine, MakeoverQuality, getMakeoverCoinCost, engineSupportsQuality } from "../../../@types/sharedSchema";
import { MAKEOVER_ENGINE_OPTIONS, MAKEOVER_QUALITY_OPTIONS } from "./makeoverModelOptions";

type MakeoverModelSelectorProps = {
  engine: MakeoverEngine;
  quality: MakeoverQuality;
  onEngineChange: (value: MakeoverEngine) => void;
  onQualityChange: (value: MakeoverQuality) => void;
};

/** Small token-cost chip; dimmed (lower opacity) when its option isn't the selected one. */
function CostChip({ cost, active }: { cost: number; active: boolean }): JSX.Element {
  return (
    <Chip
      size="small"
      label={`${cost} 🪙`}
      sx={{
        height: 18,
        fontSize: "0.62rem",
        opacity: active ? 1 : 0.4,
        "& .MuiChip-label": { px: 0.75 }
      }}
    />
  );
}

/**
 * The two cost axes: engine (performance tier) and quality (resolution). Each option shows
 * its live per-image token price as a small chip, dimmed when not selected.
 */
export function MakeoverModelSelector({
  engine,
  quality,
  onEngineChange,
  onQualityChange
}: MakeoverModelSelectorProps): JSX.Element {
  const { t: translate } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);

  // If the selected engine doesn't support the current quality, snap back to 1K. Covers both
  // switching engines and a stale stored preference.
  useEffect(() => {
    if (!engineSupportsQuality(engine, quality)) {
      onQualityChange("1K");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, quality]);

  return (
    <Stack gap={1.25} sx={{ maxWidth: 520 }}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {translate("makeovers.engine.title", "רמת המנוע והאיכות")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setHelpOpen(true)}
          aria-label={translate("makeovers.engine.helpTitle", "איך לבחור מנוע ואיכות?")}
          sx={{ p: 0.25 }}
        >
          <HelpOutlineIcon sx={{ fontSize: 16 }} color="action" />
        </IconButton>
      </Stack>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {translate("makeovers.engine.helpTitle", "איך לבחור מנוע ואיכות?")}
        </DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2">
              {translate("makeovers.engine.helpIntro", "הבחירה היא איזון בין איכות למחיר:")}
            </Typography>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {translate("makeovers.engine.helpEngineTitle", "המנוע")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  "makeovers.engine.helpEngineBody",
                  "המנוע הרגיל נותן תוצאה איכותית ונאמנה למקור במחיר הנמוך ביותר, ומתאים לרוב התמונות. המנוע החזק נותן תוצאה מלוטשת ודרמטית יותר — אך עולה יותר. כדאי להתחיל עם הרגיל, ולעבור לחזק רק אם התוצאה לא מספיקה."
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {translate("makeovers.engine.helpQualityTitle", "הרזולוציה")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(
                  "makeovers.engine.helpQualityBody",
                  "רזולוציה גבוהה (2K) חדה יותר לזום אך עולה יותר. רגיל (1K) מספיק לרוב המיני-סייטים והרשתות החברתיות."
                )}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {translate(
                "makeovers.engine.helpTip",
                "טיפ: התוצאה משתנה מתמונה לתמונה. מומלץ להריץ כמה תמונות בכל רמה ולבחור את מה שמתאים לכם ולתקציב שלכם."
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>{translate("common.gotIt", "הבנתי")}</Button>
        </DialogActions>
      </Dialog>

      {/* Engine (performance tier) — each option priced at the currently-selected quality. */}
      <RadioGroup value={engine} onChange={(e) => onEngineChange(e.target.value as MakeoverEngine)}>
        {MAKEOVER_ENGINE_OPTIONS.map((opt) => {
          const selected = opt.key === engine;
          return (
            <FormControlLabel
              key={opt.key}
              value={opt.key}
              control={
                <Radio color="primary" size="small" sx={{ alignSelf: "flex-start", py: 0.5 }} />
              }
              sx={{
                alignItems: "flex-start",
                m: 0,
                mb: 1,
                p: 1,
                borderRadius: 1.5,
                border: (theme) =>
                  `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                backgroundColor: selected ? "action.hover" : "transparent"
              }}
              label={
                <Box sx={{ py: 0.25 }}>
                  <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      <span aria-hidden style={{ marginInlineEnd: 4 }}>
                        {opt.icon}
                      </span>
                      {translate(opt.labelKey, opt.labelDefault)}
                    </Typography>
                    <CostChip cost={getMakeoverCoinCost(opt.key, quality)} active={selected} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {translate(opt.descKey, opt.descDefault)}
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </RadioGroup>

      {/* Quality (resolution) — small clickable chips, dimmed when not selected. */}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {translate("makeovers.quality.title", "רזולוציה")}
      </Typography>
      <Stack direction="row" gap={1} flexWrap="wrap">
        {MAKEOVER_QUALITY_OPTIONS.map((opt) => {
          const supported = engineSupportsQuality(engine, opt.key);
          const selected = supported && opt.key === quality;
          return (
            <Chip
              key={opt.key}
              size="small"
              clickable={supported}
              disabled={!supported}
              color="default"
              variant={selected ? "filled" : "outlined"}
              onClick={() => supported && onQualityChange(opt.key)}
              label={`${translate(opt.labelKey, opt.labelDefault)} · ${getMakeoverCoinCost(
                engine,
                opt.key
              )} 🪙`}
              sx={{
                opacity: !supported ? 0.35 : selected ? 1 : 0.5,
                ...(selected && {
                  backgroundColor: "#C8E6C9", // pastel green
                  color: "#1B5E20",
                  "&:hover": { backgroundColor: "#B6DEB8" }
                })
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
}

export default MakeoverModelSelector;
